/**
 * The meeting, while you are reading something else.
 *
 * Leaving the window is not leaving the call: the pill keeps the timer, the
 * microphone and the way back, and says if someone starts sharing while you
 * are away. Hanging up lives here and on the stage, never behind a stray
 * click on a backdrop.
 */

import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'

import styles from './CallWindow.module.css'

function elapsedSince(startedAt: string, now: number): string {
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000))
  const minutes = Math.floor(seconds / 60)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${minutes}:${pad(seconds % 60)}`
}

export function CallPill({
  where,
  startedAt,
  micEnabled,
  sharedBy,
  onToggleMic,
  onOpen,
  onLeave,
}: {
  where: string
  startedAt: string
  micEnabled: boolean
  /** Who is sharing a screen right now, if anyone. */
  sharedBy?: string | null
  onToggleMic: () => void
  onOpen: () => void
  onLeave: () => void
}): ReactElement {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <div className={styles.pill} role="status" aria-label={`In a call in ${where}`}>
      <span className={styles.pillDot} />
      <div className={styles.pillText}>
        <span className={styles.pillTitle}>
          {where} · {elapsedSince(startedAt, now)}
        </span>
        {sharedBy === null || sharedBy === undefined ? null : (
          <span className={styles.pillMeta}>{sharedBy} is sharing</span>
        )}
      </div>

      <button
        type="button"
        className={styles.pillButton}
        aria-label={micEnabled ? 'Mute' : 'Unmute'}
        aria-pressed={!micEnabled}
        onClick={onToggleMic}
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
          <rect x="7.6" y="2.8" width="4.8" height="9" rx="2.4" />
          <path d="M4.8 9.4a5.2 5.2 0 0 0 10.4 0M10 14.6V17" />
          {micEnabled ? null : <path d="M3.6 3.4 16.4 16.6" />}
        </svg>
      </button>

      <button type="button" className={styles.pillButton} aria-label="Back to the call" onClick={onOpen}>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4.6 11.4 10 6.2l5.4 5.2" />
        </svg>
      </button>

      <button type="button" className={styles.pillButton} data-tone="leave" aria-label="Leave call" onClick={onLeave}>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <path d="M3.4 12.2c3.6-3.4 9.6-3.4 13.2 0l.6-2.2c-4.2-4-10.2-4-14.4 0z" />
        </svg>
      </button>
    </div>
  )
}
